import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  Button,
  PHONE_OR_TOUCH_QUERY,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useMediaQuery,
} from '@bastion/ui';
import type { ActiveFilter, FilterConfig, FilterState } from '../types';
import { FilterMenu } from '../FilterMenu/FilterMenu';
import css from './FilterChip.module.css';

export interface FilterChipProps {
  config: FilterConfig;
  filter: ActiveFilter;
  /** Full filter state — the chip's own menu needs to read sibling chips for live counts. */
  filterState: FilterState;
  filterConfigs: FilterConfig[];
  onFilterStateChange: (next: FilterState) => void;
  onFilterChange: (next: ActiveFilter) => void;
  onRemove: () => void;
}

/**
 * On a phone or a touch screen the chip menu box takes focus when it opens,
 * not its search field. Focus in the field opens the touch keyboard over
 * the menu. With focus in the box, Tab and a screen reader go into the
 * menu. Radix calls this on the box (tabIndex -1).
 */
function focusMenuBox(event: Event) {
  event.preventDefault();
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.focus({ preventScroll: true });
  }
}

function operatorWord(op: ActiveFilter['operator']): string {
  return op === 'is' ? 'is' : 'is not';
}

function formatMultiSelectSummary(
  config: Extract<FilterConfig, { kind: 'multi-select' }>,
  values: string[],
  state: FilterState,
  active: ActiveFilter,
): string {
  if (config.formatSummary) {
    return config.formatSummary(state, active);
  }
  if (values.length === 0) {
    return '…';
  }
  if (values.length > 2) {
    return `${values.length} selected`;
  }
  return values
    .map((v) => config.options.find((o) => o.value === v)?.label ?? v)
    .join(', ');
}

function formatTextSearchSummary(values: string[]): string {
  const q = values[0]?.trim() ?? '';
  if (!q.length) {
    return '…';
  }
  if (q.length > 40) {
    return `${q.slice(0, 37)}…`;
  }
  return q;
}

function formatDateRangeSummary(values: string[]): string {
  const from = values[0]?.trim() ?? '';
  const to = values[1]?.trim() ?? '';
  if (!from && !to) {
    return '…';
  }
  if (from && to) {
    return from === to ? from.slice(0, 10) : `${from.slice(0, 10)} – ${to.slice(0, 10)}`;
  }
  if (from) {
    return `From ${from.slice(0, 10)}`;
  }
  return `To ${(to ?? '').slice(0, 10)}`;
}

function formatNumericRangeSummary(
  values: string[],
  unit: string | undefined,
  minOnly: boolean | undefined,
): string {
  const minV = values[0]?.trim() ?? '';
  const maxV = values[1]?.trim() ?? '';
  const u = unit ?? '';
  if (!minV && !maxV) {
    return '…';
  }
  if (minOnly || (!maxV && minV)) {
    return `\u2265 ${minV}${u}`;
  }
  if (!minV && maxV) {
    return `\u2264 ${maxV}${u}`;
  }
  return `${minV}\u2013${maxV}${u}`;
}

function formatTimeRangeSummary(
  config: Extract<FilterConfig, { kind: 'time-range' }>,
  values: string[],
): string {
  if (values.length === 0) {
    return '…';
  }
  if (values.length === 1) {
    const presetId = values[0]?.trim() ?? '';
    if (!presetId) return '…';
    const preset = config.presets.find((p) => p.id === presetId);
    return preset?.label ?? presetId;
  }
  // Absolute range: defer formatting to the consumer's `formatLocalDateTime`
  // (so PT semantics live with the consumer, not the chip), then trim the
  // seconds segment so it reads cleanly.
  const format = config.formatLocalDateTime ?? ((s: string) => s);
  const tz = config.timezoneLabel ? ` ${config.timezoneLabel}` : '';
  const from = values[0]?.trim() ?? '';
  const to = values[1]?.trim() ?? '';
  const fromDisplay = from ? format(from) : '';
  const toDisplay = to ? format(to) : '';
  if (!fromDisplay && !toDisplay) return '…';
  if (fromDisplay && toDisplay) {
    return `${fromDisplay} \u2013 ${toDisplay}${tz}`;
  }
  if (fromDisplay) return `From ${fromDisplay}${tz}`;
  return `To ${toDisplay}${tz}`;
}

export function FilterChip({
  config,
  filter,
  filterState,
  filterConfigs,
  onFilterStateChange,
  onFilterChange,
  onRemove,
}: FilterChipProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const phoneOrTouch = useMediaQuery(PHONE_OR_TOUCH_QUERY);
  const summary = useMemo(() => {
    if (config.kind === 'multi-select') {
      return formatMultiSelectSummary(config, filter.values, filterState, filter);
    }
    if (config.kind === 'text-search') {
      return formatTextSearchSummary(filter.values);
    }
    if (config.kind === 'numeric-range') {
      return formatNumericRangeSummary(filter.values, config.unit, config.minOnly);
    }
    if (config.kind === 'time-range') {
      return formatTimeRangeSummary(config, filter.values);
    }
    return formatDateRangeSummary(filter.values);
  }, [config, filter, filterState]);
  const Icon = config.icon;

  const showOperatorToggle =
    config.kind === 'multi-select' && config.allowOperatorToggle !== false;

  const toggleOperator = () => {
    onFilterChange({
      ...filter,
      operator: filter.operator === 'is' ? 'isNot' : 'is',
    });
  };

  return (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <div className={css.chipWrap} data-testid={`filter-chip-${config.id}`}>
          <button
            type="button"
            className={css.attrButton}
            onClick={() => setMenuOpen(true)}
            aria-label={`Edit ${config.label} filter`}
          >
            {Icon ? (
              <Icon className={css.chipIcon} style={{ width: 14, height: 14 }} />
            ) : null}
            <span className={css.chipAttr}>{config.label}</span>
          </button>
          {showOperatorToggle ? (
            <button
              type="button"
              className={css.opButton}
              onClick={(event) => {
                event.stopPropagation();
                toggleOperator();
              }}
              aria-label="Toggle operator"
            >
              {operatorWord(filter.operator)}
            </button>
          ) : null}
          <button
            type="button"
            className={css.valuesButton}
            onClick={() => setMenuOpen(true)}
            aria-label={`Edit ${config.label} values`}
          >
            <span className={css.chipVals}>{summary}</span>
          </button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={css.remove}
            aria-label={`Remove ${config.label} filter`}
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            <X style={{ width: 14, height: 14 }} />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={css.popover}
        onOpenAutoFocus={phoneOrTouch ? focusMenuBox : undefined}
      >
        <FilterMenu
          filterConfigs={filterConfigs}
          value={filterState}
          initialAttributeId={config.id}
          onChange={onFilterStateChange}
          onClose={() => setMenuOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
